import { useState } from 'react';
import type { UserProfile } from '../types';
import { getActiveTrin, trinLabel } from '../lib/curriculum';

interface UserSwitcherProps {
  users: UserProfile[];
  activeUserId: string | null;
  authenticated: boolean;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onDelete: (id: string) => void;
  onOpenAuth: () => void;
  onLogout: () => void;
  onDeleteAccount: () => void;
  onPrivacy: () => void;
  onCancel?: () => void;
}

export default function UserSwitcher({
  users,
  activeUserId,
  authenticated,
  onSelect,
  onCreate,
  onDelete,
  onOpenAuth,
  onLogout,
  onDeleteAccount,
  onPrivacy,
  onCancel,
}: UserSwitcherProps) {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(users.length === 0);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setName('');
    setCreating(false);
  };

  const confirmDelete = (user: UserProfile) => {
    const ok = window.confirm(
      `Slet profilen "${user.name}"?\n\nAl progression for denne bruger slettes permanent.`,
    );
    if (!ok) return;
    onDelete(user.id);
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6">
      <header className="text-center">
        <h1 className="text-3xl font-semibold text-strong">
          {users.length === 0 ? 'Velkommen til TouchType' : 'Vælg bruger'}
        </h1>
        <p className="mt-2 text-sm text-muted">
          {users.length === 0
            ? 'Opret en profil for at komme i gang. Derefter måler vi dit udgangspunkt og vælger et mål.'
            : 'Vælg en eksisterende profil eller opret en ny.'}
        </p>
      </header>

      <div className="flex flex-wrap justify-center gap-2 text-sm">
        {authenticated ? (
          <button type="button" onClick={onLogout} className="btn-secondary">
            Log ud
          </button>
        ) : (
          <button type="button" onClick={onOpenAuth} className="btn-secondary">
            Log ind / opret konto
          </button>
        )}
        <button type="button" onClick={onPrivacy} className="text-faint hover:text-strong">
          Privatliv og vilkår
        </button>
      </div>

      {users.length > 0 && (
        <ul className="space-y-2">
          {users.map((u) => (
            <li key={u.id}>
              <div
                className={`flex items-stretch overflow-hidden rounded-xl ring-1 transition ${
                  u.id === activeUserId
                    ? 'bg-indigo-50 ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/40'
                    : 'bg-white ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5'
                }`}
              >
                <button
                  onClick={() => onSelect(u.id)}
                  className="flex min-w-0 flex-1 items-center justify-between px-4 py-3 text-left transition hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-strong">{u.name}</p>
                      {u.role === 'tester' && (
                        <span className="rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-500/20 dark:text-amber-200">
                          Test
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-faint">
                      {trinLabel(getActiveTrin(u))} · {u.xp} XP · {u.streak.count}{' '}
                      {u.streak.count === 1 ? 'dag' : 'dage'} streak
                    </p>
                  </div>
                  <span className="ml-3 shrink-0 text-xs text-faint">
                    Sidst aktiv {formatRel(u.lastActiveAt)}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => confirmDelete(u)}
                  aria-label={`Slet profil ${u.name}`}
                  title="Slet profil"
                  className="shrink-0 border-l border-zinc-200 px-3 text-faint transition hover:bg-rose-50 hover:text-rose-600 dark:border-white/5 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {creating ? (
        <form onSubmit={submit} className="surface p-4">
          <label className="block">
            <span className="label">Navn</span>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fx Mette"
              className="field-input mt-1 w-full rounded-lg bg-white px-3 py-2 text-zinc-900 ring-1 ring-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-white/10"
            />
          </label>
          <div className="mt-3 flex gap-2">
            <button
              type="submit"
              className="flex-1 rounded-lg bg-indigo-500 px-3 py-2 font-medium text-white transition hover:bg-indigo-400"
            >
              Opret bruger
            </button>
            {users.length > 0 && (
              <button
                type="button"
                onClick={() => setCreating(false)}
                className="btn-secondary"
              >
                Annuller
              </button>
            )}
          </div>
        </form>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="rounded-xl bg-white px-4 py-3 text-strong ring-1 ring-zinc-200 transition hover:bg-zinc-50 dark:bg-zinc-900/60 dark:ring-white/5 dark:hover:bg-zinc-800/60"
        >
          + Opret ny bruger
        </button>
      )}

      {authenticated && (
        <section className="space-y-2 border-t border-zinc-200 pt-4 dark:border-white/5">
          <p className="label text-rose-600 dark:text-rose-400">Konto</p>
          <p className="text-xs text-faint">
            Sletter din konto og alle tilknyttede profiler permanent.
          </p>
          <button
            type="button"
            onClick={onDeleteAccount}
            className="w-full rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-50 dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10"
          >
            Slet konto…
          </button>
        </section>
      )}

      {onCancel && users.length > 0 && (
        <button
          onClick={onCancel}
          className="text-sm text-faint transition hover:text-strong"
        >
          Tilbage
        </button>
      )}
    </div>
  );
}

function formatRel(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
    if (days <= 0) return 'i dag';
    if (days === 1) return 'i går';
    return `for ${days} dage siden`;
  } catch {
    return '—';
  }
}
